import { Directive, ElementRef, EventEmitter, Input, NgZone, Output } from "@angular/core";
import { NgStyleInterface } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { NzTooltipDirective } from "ng-zorro-antd/tooltip";
import { Subscription, distinctUntilChanged, filter, fromEvent, takeUntil } from "rxjs";
import { UiTSType } from "@webapp/ui/core/models/ui-type.models";
import { UiTooltipPlacement, UiTooltipTrigger } from "@webapp/ui/tooltip/tooltip.models";

@Directive({
  selector: "[ui-tooltip]",
  exportAs: "uiTooltip",
  standalone: true,
})
export class UiTooltipDirective extends NzTooltipDirective {
  @Input("uiTooltipTitle") public title?: UiTSType | null;
  @Input("uiTooltipTitleContext") public titleContext?: object | null = null;
  @Input("nz-tooltip") public directiveTitle?: UiTSType | null;
  @Input("uiTooltipTrigger") public trigger?: UiTooltipTrigger = "hover";
  @Input("uiTooltipPlacement") public placement?: UiTooltipPlacement = "top";
  @Input("uiTooltipOrigin") public origin?: ElementRef<HTMLElement>;
  @Input("uiTooltipVisible") public visible = false;
  @Input("uiTooltipMouseEnterDelay") public mouseEnterDelay?: number;
  @Input("uiTooltipMouseLeaveDelay") public mouseLeaveDelay?: number;
  @Input("uiTooltipOverlayClassName") public overlayClassName?: string;
  @Input("uiTooltipOverlayStyle") public overlayStyle?: NgStyleInterface;
  @Input("uiTooltipArrowPointAtCenter") @InputBoolean() public arrowPointAtCenter?: boolean;
  @Input("uiTooltipColor") public nzTooltipColor?: string;

  @Output("uiTooltipVisibleChange") public readonly visibleChange = new EventEmitter<boolean>();

  private wheelSubscription: Subscription | null = null;
  private overlayListenerDisposables: (() => void)[] = [];

  constructor(private zone: NgZone) {
    super();

    this.visibleChange
      .pipe(distinctUntilChanged())
      .pipe(takeUntil(this.destroy$))
      .subscribe((visible: boolean) => {
        this.handleNzVisibleChange(visible);
      });
  }

  private handleNzVisibleChange(visible: boolean): void {
    if (visible) {
      this.addWheelListener();
      this.addOverlayListeners();
    } else {
      this.removeWheelListener();
      this.removeOverlayListeners();
    }
  }

  private addWheelListener(): void {
    if (this.trigger !== "hover") return;

    this.wheelSubscription = this.zone.runOutsideAngular(() => {
      return fromEvent(document, "wheel")
        .pipe(
          takeUntil(this.destroy$),
          filter(() => this.component.nzVisible)
        )
        .subscribe(() => this.hide());
    });
  }

  private removeWheelListener(): void {
    if (this.wheelSubscription) {
      this.wheelSubscription.unsubscribe();
      this.wheelSubscription = null;
    }
  }

  private addOverlayListeners(): void {
    if (this.trigger !== "hover") return;

    this.overlayListenerDisposables.push(
      this.renderer.listen(this.elementRef.nativeElement, "mouseleave", () => {
        if (!this.component?.overlay.overlayRef) return;

        const overlay = this.component.overlay.overlayRef.overlayElement;
        this.overlayListenerDisposables.push(
          this.renderer.listen(overlay, "mouseenter", () => {
            this.hide();
          })
        );
      })
    );
  }

  private removeOverlayListeners(): void {
    this.overlayListenerDisposables.forEach((dispose) => dispose());
    this.overlayListenerDisposables = [];
  }
}
